import { createFileRoute, Link } from '@tanstack/react-router'
import { SkeletonRows } from '@/components/rm'
import { useT, type MessageKey } from '@/i18n'
import { fmtTime, fmtWeekday, pesoFromCentavos } from '@/lib/ph'
import { useMyBookings, type BookingStatus } from '@/lib/bookings'
import { cn } from '@/lib/utils'

// Spec 007. The client's bookings.
export const Route = createFileRoute('/_client/bookings')({ component: MyBookings })

export const BK_STATUS: Record<BookingStatus, MessageKey> = {
  requested: 'bk.status.requested', awaiting_payment: 'bk.status.awaiting_payment', paid: 'bk.status.paid', done: 'bk.status.done',
  released: 'bk.status.released', disputed: 'bk.status.disputed', refunded: 'bk.status.refunded', cancelled: 'bk.status.cancelled', expired: 'bk.status.expired',
}
export const BK_TONE: Record<BookingStatus, string> = {
  requested: 'bg-token-info-surface text-token-info', awaiting_payment: 'bg-token-warning-surface text-token-warning',
  paid: 'bg-token-success-surface text-token-success', done: 'bg-token-warning-surface text-token-warning', released: 'bg-token-success-surface text-token-success',
  disputed: 'bg-token-danger-surface text-token-danger', refunded: 'bg-token-hover text-token-muted', cancelled: 'bg-token-hover text-token-muted', expired: 'bg-token-hover text-token-muted',
}

function MyBookings() {
  const { t } = useT()
  const q = useMyBookings()
  return (
    <div className="space-y-4">
      <h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">{t('bk.nav')}</h1>
      {q.isLoading && <SkeletonRows count={3} />}
      {!q.isLoading && (q.data ?? []).length === 0 && <p className="rounded-[16px] border border-token-subtle bg-token-surface p-5 text-[14px] text-token-secondary">{t('bk.empty')}</p>}
      <ul className="space-y-3">
        {(q.data ?? []).map((b) => (
          <li key={b.id}>
            <Link to="/bookings/$id" params={{ id: b.id }} className="block rounded-[16px] border border-token-subtle bg-token-surface p-4 hover:border-token-strong">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-semibold text-token-primary">{b.title}</p>
                  <p className="truncate text-[12.5px] text-token-secondary">{b.business_name}</p>
                </div>
                <span className="flex-none text-[14px] font-bold tabular-nums text-token-primary">{pesoFromCentavos(b.price_centavos)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-[12.5px] text-token-secondary">{t('bk.when', { date: fmtWeekday(b.scheduled_start), time: fmtTime(b.scheduled_start) })}</span>
                <span className={cn('rounded-full px-2.5 py-0.5 text-[12px] font-semibold', BK_TONE[b.status])}>{t(BK_STATUS[b.status])}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
